import { useEffect, useMemo, useState } from "react";
import { House, Users, Drop, ShirtFolded, Plus, PencilSimple, Trash, X, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CELL = 56; // px per grid cell

// ISO date of the Monday of the given date's week
function mondayIso(d = new Date()) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // 0=Mon
  dt.setDate(dt.getDate() - day);
  return dt.toISOString().slice(0, 10);
}

export default function Camp() {
  const { t } = useLang();
  const { user } = useAuth();
  const { has } = usePermissions();
  const canWrite = has("camp.write");
  const [tab, setTab] = useState("layout");

  return (
    <div className="space-y-6" data-testid="camp-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-3xl text-slate-900">{t("camp")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("campSubtitle")}</p>
        </div>
        <Button
          variant="outline"
          data-testid="camp-rooming-pdf"
          onClick={async () => {
            try {
              await downloadFile("/camp/rooming-pdf", `rooming-list-${new Date().toISOString().slice(0,10)}.pdf`);
            } catch (_e) { /* toast handled globally */ }
          }}
          className="border-blue-900 text-blue-900 hover:bg-blue-50"
        >
          <FilePdf size={16} weight="bold" className="mr-1.5" /> Rooming List PDF
        </Button>
      </header>

      <SummaryStrip />

      <div className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {[
          { id: "layout", label: t("campTabLayout"), icon: House },
          { id: "rooms", label: t("campTabRooms"), icon: Users },
          { id: "laundry", label: t("campTabLaundry"), icon: ShirtFolded },
          { id: "water", label: t("campTabWater"), icon: Drop },
        ].map((tt) => (
          <button
            key={tt.id}
            data-testid={`camp-tab-${tt.id}`}
            onClick={() => setTab(tt.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === tt.id ? "border-blue-900 text-blue-900" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <tt.icon size={16} weight="bold" /> {tt.label}
          </button>
        ))}
      </div>

      {tab === "layout" && <LayoutTab canWrite={canWrite} />}
      {tab === "rooms" && <RoomsTab canWrite={canWrite} />}
      {tab === "laundry" && <LaundryTab canWrite={canWrite} />}
      {tab === "water" && <WaterTab />}
    </div>
  );
}

// ---------------------- Summary Strip ----------------------
function SummaryStrip() {
  const { t } = useLang();
  const [sum, setSum] = useState(null);
  useEffect(() => {
    const load = () => api.get("/camp/summary").then((r) => setSum(r.data)).catch(() => setSum(null));
    load();
    const int = setInterval(load, 30_000);
    return () => clearInterval(int);
  }, []);
  if (!sum) return null;
  const cards = [
    { label: t("campRooms"), value: sum.rooms_total, testid: "camp-summary-rooms" },
    { label: t("campBeds"), value: sum.capacity_total, testid: "camp-summary-capacity" },
    { label: t("campOccupants"), value: sum.occupants_total, testid: "camp-summary-occupants" },
    { label: t("campOccupancy"), value: `${sum.occupancy_pct}%`, testid: "camp-summary-occupancy" },
    { label: t("campBlocks"), value: sum.blocks.length || "—", testid: "camp-summary-blocks" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="camp-summary">
      {cards.map((c) => (
        <div key={c.label} data-testid={c.testid} className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{c.label}</p>
          <p className="font-heading font-black text-2xl text-slate-900 mt-1">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------- Layout Tab ----------------------
function LayoutTab({ canWrite }) {
  const { t } = useLang();
  const [rooms, setRooms] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const load = async () => {
    const [r, a] = await Promise.all([api.get("/camp/rooms"), api.get("/camp/assignments")]);
    setRooms(r.data); setAssigns(a.data);
  };
  useEffect(() => { load(); }, []);

  const occupantsByRoom = useMemo(() => {
    const m = {};
    assigns.forEach((a) => { (m[a.room_id] = m[a.room_id] || []).push(a); });
    return m;
  }, [assigns]);

  const cols = 12;
  // Compute canvas height: max(y + height) among rooms, min 6
  const rows = Math.max(6, ...rooms.map((r) => (r.y || 0) + (r.height || 2)));

  if (rooms.length === 0) {
    return <EmptyState msg={t("campNoRooms")} />;
  }

  return (
    <div className="space-y-4">
      <div
        data-testid="camp-layout-canvas"
        className="relative overflow-auto bg-slate-100 rounded-lg border border-slate-300 p-4"
        style={{
          minHeight: rows * CELL + 32,
          backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.06) 1px, transparent 1px)`,
          backgroundSize: `${CELL}px ${CELL}px`,
        }}
      >
        <div className="relative" style={{ width: cols * CELL, height: rows * CELL }}>
          {rooms.map((r) => {
            const occ = occupantsByRoom[r.id] || [];
            const cap = r.capacity || 2;
            const full = occ.length >= cap;
            const empty = occ.length === 0;
            const color = empty
              ? "border-slate-400 bg-white"
              : full
              ? "border-red-500 bg-red-50"
              : "border-emerald-500 bg-emerald-50";
            return (
              <button
                key={r.id}
                data-testid={`camp-room-${r.number}`}
                onClick={() => setSelected(r)}
                className={`absolute rounded-md border-2 ${color} flex flex-col items-start justify-between p-2 text-left hover:ring-2 hover:ring-blue-400 transition-all`}
                style={{
                  left: (r.x || 0) * CELL,
                  top: (r.y || 0) * CELL,
                  width: (r.width || 2) * CELL - 6,
                  height: (r.height || 2) * CELL - 6,
                }}
              >
                <div className="w-full">
                  <p className="font-heading font-black text-lg text-slate-900 leading-none">#{r.number}</p>
                  {r.block && <p className="text-[10px] uppercase tracking-wide font-bold text-slate-500">{r.block}</p>}
                </div>
                <div className="w-full flex items-center justify-between">
                  <span className={`text-xs font-bold ${full ? "text-red-700" : empty ? "text-slate-500" : "text-emerald-700"}`}>
                    {occ.length}/{cap}
                  </span>
                  <div className="flex gap-0.5">
                    {Array.from({ length: cap }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-4 rounded-sm ${i < occ.length ? (full ? "bg-red-500" : "bg-emerald-500") : "bg-slate-300"}`}
                      />
                    ))}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Legend />

      {selected && (
        <RoomDetailPanel
          room={selected}
          occupants={occupantsByRoom[selected.id] || []}
          canWrite={canWrite}
          onClose={() => setSelected(null)}
          onAssignClick={() => setAssignOpen(true)}
          onReload={load}
        />
      )}

      {assignOpen && selected && (
        <AssignDialog
          room={selected}
          onClose={() => setAssignOpen(false)}
          onDone={() => { setAssignOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-slate-600">
      <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm border-2 border-slate-400 bg-white" /> Vacía</span>
      <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm border-2 border-emerald-500 bg-emerald-50" /> Parcial</span>
      <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm border-2 border-red-500 bg-red-50" /> Llena</span>
    </div>
  );
}

function RoomDetailPanel({ room, occupants, canWrite, onClose, onAssignClick, onReload }) {
  const { t } = useLang();
  const cap = room.capacity || 2;
  const release = async (id) => {
    await api.delete(`/camp/assignments/${id}`);
    toast.success("Liberada");
    onReload();
  };
  return (
    <div data-testid="camp-room-detail" className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500">{t("campTabRooms")}</p>
          <h3 className="font-heading font-black text-2xl text-slate-900">
            #{room.number} {room.block && <span className="text-slate-500 text-lg font-bold">· {room.block}</span>}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {occupants.length}/{cap} {t("campBeds")} — {t("campOccupancy")}: {Math.round(occupants.length / cap * 100)}%
          </p>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="camp-room-detail-close">
          <X size={20} weight="bold" />
        </button>
      </div>
      <div className="mt-4 space-y-2">
        {occupants.map((a) => (
          <div key={a.id} className="flex items-center justify-between rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
            <div>
              <p className="font-semibold text-sm text-slate-900">{a.employee?.name || "—"}</p>
              <p className="text-[11px] text-slate-500">
                {a.employee?.position || ""}{a.employee?.crew ? ` · ${a.employee.crew}` : ""}
                {a.bed_label ? ` · ${a.bed_label}` : ""}
              </p>
            </div>
            {canWrite && (
              <button
                onClick={() => release(a.id)}
                data-testid={`camp-release-${a.id}`}
                className="text-xs font-bold text-red-600 hover:text-red-800"
              >
                {t("campReleaseOccupant")}
              </button>
            )}
          </div>
        ))}
        {occupants.length === 0 && <p className="text-sm text-slate-500 italic">{t("campEmpty")}</p>}
      </div>
      {canWrite && occupants.length < cap && (
        <Button className="mt-4" onClick={onAssignClick} data-testid="camp-assign-open">
          <Plus size={16} className="mr-1" /> {t("campAssignOccupant")}
        </Button>
      )}
    </div>
  );
}

function AssignDialog({ room, onClose, onDone }) {
  const { t } = useLang();
  const [avail, setAvail] = useState([]);
  const [empId, setEmpId] = useState("");
  const [bed, setBed] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/camp/available-employees").then((r) => setAvail(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!empId) { toast.error("Selecciona un colaborador"); return; }
    setSaving(true);
    try {
      await api.post("/camp/assignments", { room_id: room.id, employee_id: empId, bed_label: bed });
      toast.success("Asignado");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="camp-assign-dialog">
        <DialogHeader>
          <DialogTitle>{t("campAssignOccupant")} · #{room.number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t("campSelectEmployee")}</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="camp-assign-employee"><SelectValue placeholder={t("campSelectEmployee")} /></SelectTrigger>
              <SelectContent>
                {avail.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} {e.dni ? `· ${e.dni}` : ""}{e.position ? ` · ${e.position}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("campBedLabel")}</Label>
            <Input value={bed} onChange={(e) => setBed(e.target.value)} placeholder="cama 1 / litera sup." data-testid="camp-assign-bed" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="camp-assign-save">{saving ? "…" : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------- Rooms Tab (CRUD) ----------------------
function RoomsTab({ canWrite }) {
  const { t } = useLang();
  const [rooms, setRooms] = useState([]);
  const [dialog, setDialog] = useState(null); // {mode:'new'|'edit', room?}
  const load = () => api.get("/camp/rooms").then((r) => setRooms(r.data));
  useEffect(() => { load(); }, []);
  const del = async (r) => {
    if (!confirm(`¿Eliminar habitación #${r.number}?`)) return;
    await api.delete(`/camp/rooms/${r.id}`);
    toast.success("Eliminada");
    load();
  };
  return (
    <div className="space-y-3">
      {canWrite && (
        <div className="flex justify-end">
          <Button data-testid="camp-new-room" onClick={() => setDialog({ mode: "new" })}>
            <Plus size={16} className="mr-1" /> {t("campNewRoom")}
          </Button>
        </div>
      )}
      {rooms.length === 0 && <EmptyState msg={t("campNoRooms")} />}
      <div className="grid gap-2">
        {rooms.map((r) => (
          <div key={r.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-4 py-3" data-testid={`camp-room-row-${r.number}`}>
            <div>
              <p className="font-bold text-slate-900">#{r.number} {r.block && <span className="text-slate-500 font-normal">· {r.block}</span>}</p>
              <p className="text-xs text-slate-500">Piso {r.floor} · {r.capacity} camas · {r.width}×{r.height} celdas {r.notes && `· ${r.notes}`}</p>
            </div>
            {canWrite && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setDialog({ mode: "edit", room: r })} data-testid={`camp-edit-room-${r.number}`}>
                  <PencilSimple size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => del(r)} className="text-red-600" data-testid={`camp-delete-room-${r.number}`}>
                  <Trash size={16} />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {dialog && (
        <RoomDialog
          mode={dialog.mode}
          initial={dialog.room}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function RoomDialog({ mode, initial, onClose, onDone }) {
  const { t } = useLang();
  const [f, setF] = useState({
    number: initial?.number || "",
    block: initial?.block || "",
    floor: initial?.floor ?? 0,
    capacity: initial?.capacity || 2,
    width: initial?.width || 2,
    height: initial?.height || 2,
    x: initial?.x ?? null,
    y: initial?.y ?? null,
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.number.trim()) { toast.error("El número es requerido"); return; }
    setSaving(true);
    try {
      if (mode === "edit") await api.put(`/camp/rooms/${initial.id}`, f);
      else await api.post("/camp/rooms", f);
      toast.success("Guardado");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="camp-room-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? t("campEditRoom") : t("campNewRoom")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t("campRoomNumber")} *</Label><Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="camp-room-number" /></div>
          <div><Label>{t("campRoomBlock")}</Label><Input value={f.block} onChange={(e) => setF({ ...f, block: e.target.value })} placeholder="A / B / Modulo 1" data-testid="camp-room-block" /></div>
          <div><Label>{t("campRoomFloor")}</Label><Input type="number" value={f.floor} onChange={(e) => setF({ ...f, floor: parseInt(e.target.value || "0", 10) })} data-testid="camp-room-floor" /></div>
          <div><Label>{t("campRoomCapacity")}</Label><Input type="number" min={1} max={20} value={f.capacity} onChange={(e) => setF({ ...f, capacity: parseInt(e.target.value || "1", 10) })} data-testid="camp-room-capacity" /></div>
          <div><Label>{t("campRoomWidth")}</Label><Input type="number" min={1} max={8} value={f.width} onChange={(e) => setF({ ...f, width: parseInt(e.target.value || "2", 10) })} data-testid="camp-room-width" /></div>
          <div><Label>{t("campRoomHeight")}</Label><Input type="number" min={1} max={8} value={f.height} onChange={(e) => setF({ ...f, height: parseInt(e.target.value || "2", 10) })} data-testid="camp-room-height" /></div>
          <div><Label>X (opcional)</Label><Input type="number" min={0} value={f.x ?? ""} onChange={(e) => setF({ ...f, x: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></div>
          <div><Label>Y (opcional)</Label><Input type="number" min={0} value={f.y ?? ""} onChange={(e) => setF({ ...f, y: e.target.value === "" ? null : parseInt(e.target.value, 10) })} /></div>
          <div className="col-span-2"><Label>{t("campRoomNotes")}</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="camp-room-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="camp-room-save">{saving ? "…" : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------- Laundry Tab ----------------------
function LaundryTab({ canWrite }) {
  const { t } = useLang();
  const [weekStart, setWeekStart] = useState(mondayIso());
  const [slots, setSlots] = useState([]);
  const [rooms, setRooms] = useState([]);
  const load = async () => {
    const [s, r] = await Promise.all([
      api.get("/camp/laundry", { params: { week_start: weekStart } }),
      api.get("/camp/rooms"),
    ]);
    setSlots(s.data); setRooms(r.data);
  };
  useEffect(() => { load(); }, [weekStart]);

  const blocks = useMemo(() => {
    const s = new Set(rooms.map((r) => r.block || ""));
    return Array.from(s).sort();
  }, [rooms]);

  const slotFor = (dow, block) =>
    slots.find((s) => s.day_of_week === dow && (s.block || "") === (block || ""));

  const saveSlot = async (dow, block, responsible) => {
    await api.post("/camp/laundry", { week_start: weekStart, day_of_week: dow, block: block || "", responsible });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label>{t("campLaundryWeekOf")}</Label>
          <Input type="date" value={weekStart} onChange={(e) => setWeekStart(mondayIso(e.target.value))} data-testid="camp-laundry-week" />
        </div>
        <p className="text-xs text-slate-500 max-w-lg">{t("campLaundryHint")}</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" data-testid="camp-laundry-table">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-3 py-2 text-left font-bold text-slate-700">{t("campRoomBlock")}</th>
              {[0, 1, 2, 3, 4, 5, 6].map((dow) => (
                <th key={dow} className="px-3 py-2 text-center font-bold text-slate-700">{t(`dayShort${dow}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(blocks.length ? blocks : [""]).map((block) => (
              <tr key={block} className="border-b border-slate-200">
                <td className="px-3 py-2 font-semibold text-slate-900">{block || "General"}</td>
                {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
                  const slot = slotFor(dow, block);
                  return (
                    <td key={dow} className="px-2 py-2">
                      <Input
                        defaultValue={slot?.responsible || ""}
                        disabled={!canWrite}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (slot?.responsible || "")) saveSlot(dow, block, v);
                        }}
                        placeholder="—"
                        className="text-xs h-8"
                        data-testid={`camp-laundry-${block || "general"}-${dow}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------- Water Tab ----------------------
function WaterTab() {
  const { t } = useLang();
  const [lpp, setLpp] = useState(150);
  const [days, setDays] = useState(30);
  const [manualHc, setManualHc] = useState("");
  const [result, setResult] = useState(null);
  const [standards, setStandards] = useState([]);

  useEffect(() => {
    api.get("/camp/water/standards").then((r) => setStandards(r.data)).catch(() => {});
    compute();
  }, []);

  const compute = async () => {
    const body = {
      liters_per_person: parseFloat(lpp) || 0,
      days: parseInt(days, 10) || 1,
    };
    if (manualHc !== "") body.headcount = parseInt(manualHc, 10);
    const r = await api.post("/camp/water/calc", body);
    setResult(r.data);
  };

  const sourceLabel = (s) =>
    s === "camp_occupants" ? t("campSourceCamp") : s === "hr_active" ? t("campSourceHr") : t("campSourceManual");

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="camp-water-panel">
        <h3 className="font-heading font-black text-lg text-slate-900">{t("campWaterTitle")}</h3>
        <p className="text-xs text-slate-500 mt-1">{t("campWaterHint")}</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <div>
            <Label>{t("campWaterLitersPerson")}</Label>
            <Input type="number" min={10} value={lpp} onChange={(e) => setLpp(e.target.value)} data-testid="camp-water-lpp" />
          </div>
          <div>
            <Label>{t("campWaterDays")}</Label>
            <Input type="number" min={1} max={90} value={days} onChange={(e) => setDays(e.target.value)} data-testid="camp-water-days" />
          </div>
          <div>
            <Label>{t("campWaterHeadcount")} <span className="text-slate-400 text-[10px]">(opcional)</span></Label>
            <Input type="number" value={manualHc} onChange={(e) => setManualHc(e.target.value)} placeholder="auto" data-testid="camp-water-headcount" />
          </div>
          <div className="flex items-end">
            <Button onClick={compute} className="w-full" data-testid="camp-water-compute">{t("campWaterCompute")}</Button>
          </div>
        </div>

        {result && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5" data-testid="camp-water-result">
            <MetricCard label={t("campWaterHeadcount")} value={`${result.headcount} personas`} sub={sourceLabel(result.source)} />
            <MetricCard label={t("campWaterDaily")} value={`${result.daily_liters.toLocaleString()} L`} sub={`${result.daily_m3} m³/día`} />
            <MetricCard label={t("campWaterPeriod")} value={`${result.period_liters.toLocaleString()} L`} sub={`${result.period_m3} m³ (${result.days} días)`} />
            <p className="col-span-full text-[11px] font-mono text-slate-500 bg-slate-50 px-3 py-2 rounded">
              <b>{t("campWaterFormula")}:</b> {result.formula}
            </p>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-heading font-black text-lg text-slate-900 mb-3">{t("campWaterStandards")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {standards.map((s) => (
            <button
              key={s.code}
              onClick={() => { setLpp(s.liters); }}
              data-testid={`camp-water-std-${s.code}`}
              className="text-left rounded-lg border border-slate-200 bg-white p-4 hover:border-blue-400 hover:shadow-sm transition-all"
            >
              <div className="flex items-baseline justify-between">
                <p className="font-semibold text-slate-900">{s.label_es}</p>
                <p className="font-heading font-black text-xl text-blue-900">{s.liters} L</p>
              </div>
              <p className="text-xs text-slate-500 mt-1">{s.note}</p>
              <p className="text-[10px] uppercase tracking-widest text-blue-600 font-bold mt-2">Click para usar</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 p-4">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
      <p className="font-heading font-black text-2xl text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
      <House size={48} weight="thin" className="mx-auto text-slate-400" />
      <p className="mt-3 text-sm text-slate-500">{msg}</p>
    </div>
  );
}
